import {
  AfterViewInit,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  ViewChild,
  Directive,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';

import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';

import { MatTableDataSource } from '@angular/material/table';

import { TranslocoService } from '@jsverse/transloco';

import { AuthService } from 'app/providers/bridge-api/auth.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from 'app/providers/semantria-api/semantria-users.service';

import { SimpleDialogButtonColor } from '../../../../components/simple-dialog.component';
import { GroupService } from '../../../../providers/semantria-api/group.service';
import { AlertService } from '../../../../services/alert.service';
import { DialogService } from '../../../../services/dialog.service';

@Directive()
export abstract class PermissionTableComponent<C, I> implements OnInit, OnChanges, AfterViewInit {
  @Input() userMemberships: Map<string, Map<string, string[]>>;
  @Input() remainingItems: I[];
  @Input() parent: C;
  @Input() parentAllUsers: SemantriaUser[];
  @Input() tableData: I[];

  @Input() scrollbarHeight = '40vh';
  @Input() hasEmail: boolean;
  @Input() isLoading: boolean;

  @Output() addItem = new EventEmitter<void>();
  @Output() itemRemoved = new EventEmitter<I>();
  @Output() permissionChanged = new EventEmitter<void>();

  @ViewChild(MatSort, { static: false }) matSort: MatSort;
  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;

  dataSource: MatTableDataSource<Partial<I>>;
  displayedColumns = ['name', 'executive', 'read', 'all', 'actions'];

  itemType: string;
  itemCount: number;
  isSaving: boolean;

  protected sortFnFactory: (sort, direction) => (a, b) => number;
  protected propSort = (a, b, prop, direction) => {
    if (!a[prop]) {
      if (!b[prop]) {
        return 0;
      }

      return -1 * direction;
    } else if (!b[prop]) {
      return direction;
    }

    return a[prop].localeCompare(b[prop]) * direction;
  };

  protected constructor(
    protected groupService: GroupService,
    protected userService: SemantriaUsersService,
    protected dialogService: DialogService,
    protected authService: AuthService,
    protected alertService: AlertService,
    protected changeDetector: ChangeDetectorRef,
    protected translocoService: TranslocoService
  ) {
    this.dataSource = new MatTableDataSource([]);
  }

  ngOnInit() {
    if (this.hasEmail) {
      this.displayedColumns.splice(1, 0, 'email');
    }
    this.dataSource.sortData = (data, sort: MatSort) => {
      const direction = sort.direction === 'asc' ? 1 : -1;

      return data.sort(this.sortFnFactory(sort, direction));
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.tableData) {
      this.dataSource.data = this.tableData;
      this.changeDetector.detectChanges();
    }
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.matSort;
  }

  canChangeUserPermission(): boolean {
    return !this.authService.isReadOnly && !this.isSaving;
  }

  canAddItem() {
    if (this.authService.isReadOnly) {
      return false;
    }
    return !this.isLoading && this.remainingItems.length > 0;
  }

  addItemClicked() {
    this.addItem.emit();
  }

  canRemoveItem(item) {
    if (this.authService.isReadOnly) {
      return false;
    }
    if (this.userMemberships.has(this.getUserId(item))) {
      return this.userMemberships.get(this.getUserId(item)).size > 1;
    }
  }

  removeItemClicked(item) {
    this.dialogService
      .showWarningPopup({
        title: this.translocoService.translate('permission-table.remove-user'),
        content: this.translocoService.translate('permission-table.remove-user-content'),
        confirmTitle: this.translocoService.translate('permission-table.remove-confirm-title'),
        cancelTitle: this.translocoService.translate('permission-table.remove-cancel-title'),
        confirmColor: SimpleDialogButtonColor.red,
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.removeItem(item);
        }
      });
  }

  userHasPermission(item, ...permissions) {
    if (
      !this.userMemberships.has(this.getUserId(item)) ||
      !this.userMemberships.get(this.getUserId(item)).has(this.getGroupId(item))
    ) {
      return false;
    }

    const groupPermissions = this.userMemberships
      .get(this.getUserId(item))
      .get(this.getGroupId(item));

    const result =
      (!groupPermissions.length && !permissions.length) ||
      (permissions.length >= 1 &&
        permissions.every((p) => groupPermissions.includes(p)) &&
        groupPermissions.every((p) => permissions.includes(p)));
    return result;
  }

  async setUserPermissions(item, ...permissions) {
    this.isSaving = true;

    const currentPermissions = SemantriaUsersService.getGroupPermissions(
      this.getUser(item),
      this.getGroupId(item)
    );

    const permissionsToAdd = permissions.filter(
      (p) => !currentPermissions.some((cp) => cp.permission_type === p)
    );
    const permissionsToRemove = currentPermissions.filter(
      (p) => !permissions.includes(p.permission_type)
    );
    const removedPermissions = [];
    const addedPermissions = [];

    try {
      for (const permission of permissionsToRemove) {
        await this.userService.removePermission(this.getUserId(item), permission.permission_id);
        removedPermissions.push(permission);
      }

      for (const permission of permissionsToAdd) {
        const newPermission = await this.userService.addPermission(
          this.getUserId(item),
          permission,
          this.getGroupId(item)
        );
        addedPermissions.push(newPermission);
      }

      this.alertService.showToast(
        this.translocoService.translate('permission-table.set-permission')
      );
      this.isSaving = false;
      this.permissionChanged.emit();
    } catch (err) {
      for (const permission of removedPermissions) {
        await this.userService.addPermission(
          permission.user_id,
          permission.permission_type,
          permission.group_id
        );
      }

      for (const permission of addedPermissions) {
        await this.userService.removePermission(this.getUserId(item), permission.permission_id);
      }

      this.isSaving = false;
      this.alertService.showErrorToast(
        this.translocoService.translate('permission-table.set-permission-failed'),
        err
      );
    }
  }

  async removeItem(item) {
    try {
      await this.groupService.removeGroupMember(this.getGroupId(item), this.getUserId(item));

      this.itemRemoved.emit(item);
      this.alertService.showToast(this.translocoService.translate('permission-table.user-removed'));
    } catch (err) {
      await this.groupService.addGroupMember(this.getGroupId(item), this.getUserId(item));

      this.alertService.showErrorToast(
        this.translocoService.translate('permission-table.user-removed-failure'),
        err
      );
    }
  }

  abstract getName(item: I);

  abstract getEmail(item: I);

  abstract getId(item: I);

  abstract getUser(item);

  abstract getUserId(item);

  abstract getGroupId(item);

  get addToolTip() {
    const translateKey = this.canAddItem()
      ? 'permission-table.can-add'
      : 'permission-table.no-more-to-add';
    return this.translocoService.translate(translateKey, {
      type:
        this.itemType === 'users'
          ? this.translocoService.translate('permission-table.item-type-users')
          : this.translocoService.translate('permission-table.item-type-user-groups'),
    });
  }
}
